import React, { useState, useEffect, useRef } from "react";
import { PublicPhoto } from "@/services";
import { Tooltip } from "@/components";
import "./PhotoSlider.css";

interface PhotoSliderProps {
  photos: PublicPhoto[];
  maxPhotos?: number;
  onAddClick: () => void;
  isLoading: boolean;
  onDeletePhoto?: (photoUrl: string) => void;
  showDelete?: boolean;
}

const getVisibleCount = () => {
  if (typeof window === "undefined") return 6;
  const width = window.innerWidth;
  if (width <= 600) return 3;
  if (width <= 1024) return 4;
  return 6;
};

const PhotoSlider: React.FC<PhotoSliderProps> = ({
  photos,
  maxPhotos = 12,
  onAddClick,
  isLoading,
  onDeletePhoto,
  showDelete = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxVisible, setMaxVisible] = useState(getVisibleCount);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const dragStartRef = useRef(0);
  const draggingRef = useRef(false);
  const dragOffsetRef = useRef(0);

  const totalPhotos = photos.length;
  const isMaxReached = totalPhotos >= maxPhotos;
  const maxIndex = Math.max(totalPhotos - (maxVisible - 1), 0);

  const canGoNext = currentIndex < totalPhotos - (maxVisible - 1);
  const canGoPrev = currentIndex > 0;

  const handleNext = () => canGoNext && setCurrentIndex((prev) => prev + 1);
  const handlePrev = () => canGoPrev && setCurrentIndex((prev) => prev - 1);

  const beginDrag = (clientX: number) => {
    if (isMobile) return;
    draggingRef.current = true;
    dragStartRef.current = clientX;
    setIsDragging(true);
  };

  const updateDrag = (clientX: number) => {
    if (!draggingRef.current || isMobile) return;
    const delta = clientX - dragStartRef.current;
    dragOffsetRef.current = delta;
    setDragOffset(delta);
  };

  const endDrag = () => {
    if (!draggingRef.current || isMobile) return;
    draggingRef.current = false;
    const delta = dragOffsetRef.current;
    if (delta > 30 && canGoPrev) handlePrev();
    else if (delta < -30 && canGoNext) handleNext();
    setDragOffset(0);
    setIsDragging(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setMaxVisible(getVisibleCount());
      setIsMobile(window.innerWidth <= 600);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile && currentIndex > maxIndex) setCurrentIndex(maxIndex);
  }, [maxIndex, currentIndex, isMobile]);

  const AddCard = () => (
    <div
      className={`photo-upload-slider ${isMaxReached ? "disabled" : ""} ${isLoading ? "loading-state" : ""}`}
      onClick={!isMaxReached ? onAddClick : undefined}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
      <span>Adicionar</span>
    </div>
  );

  const PhotoItem = ({ item, showDelete, onDelete }: any) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const url = typeof item === "string" ? item : item.url;

    return (
      <div className="photo-item-container-slider">
        {!isImageLoaded && (
          <div className="loader-overlay">
            <div className="loader-spinner"></div>
          </div>
        )}

        <img
          src={url}
          alt=""
          className={`profile-photo-thumb ${isImageLoaded ? "loaded" : "loading"}`}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          onLoad={() => setIsImageLoaded(true)}
        />

        <div>
          {!item.approved && (
            <Tooltip text="Esta foto está em análise e será liberada em breve.">
              <div className="private-badge">🔒</div>
            </Tooltip>
          )}

          {showDelete && (
            <button
              className="delete-photo-btn"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`photo-slider-container ${isMobile ? "mobile-view" : "desktop-view"}`}
    >
      <div className="photo-slider-row">
        <div className="photo-slider-track">
          {!isMobile && canGoPrev && (
            <button className="slider-nav-btn prev" onClick={handlePrev}>
              ‹
            </button>
          )}
          {!isMobile && canGoNext && (
            <button className="slider-nav-btn next" onClick={handleNext}>
              ›
            </button>
          )}

          <div
            className={`photo-slider-wrapper ${isDragging ? "is-dragging" : ""}`}
            onMouseDown={(e) => e.button === 0 && beginDrag(e.clientX)}
            onMouseMove={(e) => updateDrag(e.clientX)}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
          >
            <div
              className="photos-grid"
              style={
                !isMobile
                  ? {
                      transform: `translateX(calc(-${(100 / maxVisible) * currentIndex}% + ${dragOffset}px))`,
                    }
                  : undefined
              }
            >
              {photos.map((item, idx) => {
                const url = typeof item === "string" ? item : item.url;

                return (
                  <div key={idx} className="slider-item">
                    <PhotoItem
                      item={item}
                      showDelete={showDelete}
                      onDelete={() => onDeletePhoto?.(url)}
                    />
                  </div>
                );
              })}

              {isMobile && (
                <div className="slider-item">
                  <AddCard />
                </div>
              )}
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className="photo-add-card">
            <AddCard />
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoSlider;
